import cx from 'classnames';
import * as React from 'react';
import { getId } from '../helper';
import { isDefined } from '../helper/typeguard';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { CoverageBoxContext, ICoverageBoxContext } from './CoverageBoxContext';
import { CoverageBoxDropdownSection } from './CoverageBoxDropdownSection';
import { CoverageBoxToggleButton } from './CoverageBoxToggleButton';

interface ICoverageBoxProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  titleContent?: React.ReactNode;
  /** id to be used for accessibility attributes */
  id?: string;
  icon?: 'included' | 'excluded' | 'upgrade' | 'person-both';
  /**
   * state if the icon will be responsive
   * @default true
   */
  iconIsResponsive?: boolean;
  /** the topdk svg icon ref, e.g. '#bus' */
  svgIcon?: string;
  /**
   * size of the svg icon, which can only be set if `iconIsResponsive`is false
   * @default 'small'
   */
  svgIconSize?: 'small' | 'medium' | 'large';
  /** actions associated with this CoverageBox */
  actions?: React.ReactNode;
  /** variant of the actions */
  actionVariant?: 'add-default' | 'add-grey' | 'link' | 'manual-change' | 'standard';
  /** text next to action */
  actionSubtext?: React.ReactNode;
  /** content of the collapsible section */
  content?: React.ReactNode;
  toggleBtnContent?: React.ReactNode;
  /** content of the toggle button when it is expanded. Will show `toggleBtnContent` is not specified */
  expandedToggleBtnContent?: React.ReactNode;
  /** state if the details should be expanded by default */
  expandedByDefault?: boolean;
  /** `CoverageBoxDropdownSection` elements for this Box. Use `content` props if you only have one section. */
  dropdownSections?: React.ReactNode;
  /** `CoverageBoxToggleButton` elements for this Box. Use `toggleBtnContent` and/or `expandedToggleBtnContent` props if you only have one section */
  toggleButtons?: React.ReactNode;
  /** id for the section that is expanded initially. Use `expandedByDefault` if you only have one section. */
  defaultExpandedSectionId?: string;
  /** `<CoverageBoxPills />` to be shown. If you have multiple pills, wrap them with `React.Fragment` */
  pills?: React.ReactNode;
}

/**
 * `CoverageBox` is a component that allows you to show single/multiple collapsible sections.
 * - For single collapsible section, use `content`, `toggleBtnContent`, `expandedToggleBtnContent`, and `expandedByDefault` props
 * - For multiple collapsible sections, use `dropdownSections` (with `CoverageBoxDropdownSection`), `toggleButtons` (with `CoverageBoxToggleButton`), and `defaultExpandedSectionId` props.
 */
export class CoverageBox extends React.Component<ICoverageBoxProps, ICoverageBoxContext> {
  id = this.props.id || getId();

  expandSection = (sectionId: string | null) =>
    this.setState({
      expandedSectionId: sectionId,
    });

  state = {
    expandSection: this.expandSection,
    expandedSectionId:
      this.props.defaultExpandedSectionId || (this.props.expandedByDefault ? `${this.id}-details` : null),
  };

  render() {
    const { svgIconSize = 'small', iconIsResponsive = true, ...props } = this.props;
    const hasIcon = !!(props.icon || props.svgIcon);
    const isSingleCollapsible = isDefined(props.content);

    return (
      <CoverageBoxContext.Provider value={this.state}>
        <div
          id={this.id}
          className={cx('flos-ResponsiveCoverage Box', this.state.expandedSectionId !== null && 'expanded')}
        >
          <div className="flos-ResponsiveCoverage-header">
            <div className="flos-ResponsiveCoverage-wrapper">
              {hasIcon &&
                (iconIsResponsive ? (
                  <div
                    className={cx(
                      'flos-ResponsiveCoverage-icon',
                      props.icon && `flos-ResponsiveCoverage-icon--${props.icon}`
                    )}
                  >
                    {props.svgIcon && <SvgIcon size="small" iconHref={props.svgIcon} />}
                  </div>
                ) : (
                  props.svgIcon && (
                    <SvgIcon className="tds-icon--accordion hidden-xs" size={svgIconSize} iconHref={props.svgIcon} />
                  )
                ))}
              <div
                className={cx(
                  'flos-ResponsiveCoverage-titlegroup',
                  hasIcon && !iconIsResponsive && 'flos-ResponsiveCoverage-titlegroup--icon'
                )}
              >
                {props.title && (
                  <h3 className="flos-ResponsiveCoverage-title" id={`${this.id}-header`}>
                    {props.title}
                  </h3>
                )}
                {props.subtitle && (
                  <p
                    className={
                      iconIsResponsive ? 'flos-ResponsiveCoverage-subtitle' : 'flos-ResponsiveProduct-subtitle'
                    }
                  >
                    {props.subtitle}
                  </p>
                )}
                {props.titleContent}
                {!iconIsResponsive && (
                  <ToggleButtonsSection
                    id={this.id}
                    iconIsResponsive={iconIsResponsive}
                    isSingleCollapsible={isSingleCollapsible}
                    toggleBtnContent={props.toggleBtnContent}
                    expandedToggleBtnContent={props.expandedToggleBtnContent}
                    toggleButtons={props.toggleButtons}
                  />
                )}
              </div>
              {props.actions && (
                <div className="flos-ResponsiveCoverage-action hidden-xs">
                  <div className={cx('action', props.actionVariant)}>{props.actions}</div>
                </div>
              )}
            </div>
            <div className="flos-ResponsiveCoverage-wrapper flos-ResponsiveCoverage-wrapper--space-between">
              {iconIsResponsive && (
                <ToggleButtonsSection
                  id={this.id}
                  iconIsResponsive={iconIsResponsive}
                  isSingleCollapsible={isSingleCollapsible}
                  toggleBtnContent={props.toggleBtnContent}
                  expandedToggleBtnContent={props.expandedToggleBtnContent}
                  toggleButtons={props.toggleButtons}
                />
              )}
              {props.actionSubtext && (
                <div className="action-sub-text text-center hidden-xs">{props.actionSubtext}</div>
              )}
            </div>
          </div>
          {isSingleCollapsible && (
            <CoverageBoxDropdownSection id={`${this.id}-details`} aria-labelledby={`${this.id}-header`}>
              {props.content}
            </CoverageBoxDropdownSection>
          )}
          {props.dropdownSections}
          {props.actions && (
            <div className="flos-ResponsiveCoverage-action flos-ResponsiveCoverage-action--space-between visible-xs">
              <div className={cx('action', props.actionVariant)}>{props.actions}</div>
              {props.actionSubtext && <div className="action-sub-text">{props.actionSubtext}</div>}
            </div>
          )}
          {props.pills && (
            <div className="Box--adv__body">
              <section className="Section Section--low">
                <div className="checked-pills-wrapper">{props.pills}</div>
              </section>
            </div>
          )}
        </div>
      </CoverageBoxContext.Provider>
    );
  }
}

interface IToggleButtonSectionProps {
  isSingleCollapsible: boolean;
  id: string;
}

const ToggleButtonsSection: React.FC<
  IToggleButtonSectionProps &
    Pick<ICoverageBoxProps, 'toggleBtnContent' | 'expandedToggleBtnContent' | 'toggleButtons' | 'iconIsResponsive'>
> = ({ isSingleCollapsible, id, toggleBtnContent, expandedToggleBtnContent, iconIsResponsive, toggleButtons }) => (
  <div className={cx('flos-ResponsiveCoverage-links', !iconIsResponsive && 'flos-ResponsiveProduct-links')}>
    {isSingleCollapsible && (
      <CoverageBoxToggleButton
        sectionId={`${id}-details`}
        label={toggleBtnContent}
        expandedLabel={expandedToggleBtnContent}
      />
    )}
    {toggleButtons}
  </div>
);
